import React from "react";
import axios from "axios";
import { Button, Form, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "react-query";

import ModalSpecial from "../../../../components/Layout/Modals/ModalSpecial";
import { convertToCodeTreeData } from "../../../../helpers/ConvertToTreeData";
import { blockTypeLabel } from "../components/blockTypes";
import BlockBanners from "../components/BlockBanners";
import BlockFields from "../components/BlockFields";
import BlockSkus from "../components/BlockSkus";
import { showApiError } from "../components/apiErrors";
import {
  COLUMN_TYPES,
  DEFAULT_BACKGROUND,
  DEFAULT_ITEMS_LIMIT,
  DEFAULT_TEXT_ALIGN,
  DEFAULT_TEXT_COLOR,
  HOVER_ANIMATION_TYPES,
  ITEM_TYPES,
  LOCAL_NAME_TYPES,
  SKU_TYPES,
  TEXT_ALIGN_TYPES,
  VALIDITY_TYPES,
  appendBlockItems,
  appendBlockSkus,
  hydrateBlockBanners,
  hydrateBlockSkus,
  makeBlockBanner,
  normalizeDateForApi,
  paddingValue,
  startsWithBanner,
  toMomentRange,
} from "../components/blockItems";

const FIELD_NAMES = {
  title: "title",
  systemName: "system_name",
  background: "background_color",
  columns: "columns_count",
  category: "category_code",
  itemsLimit: "items_limit",
  localName: "local_name",
  validity: "validity",
  textColor: "text_color",
  textAlign: "text_align",
  paddingTop: "padding_top",
  paddingBottom: "padding_bottom",
  paddingLeft: "padding_left",
  paddingRight: "padding_right",
};

const ModalBlock = ({ onClose, modalOpt = { type: null } }) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isEdit = modalOpt?.type === "edit_block";

  const [blockType, setBlockType] = React.useState(
    modalOpt?.record?.block_type
  );
  const [backgroundColor, setBackgroundColor] = React.useState(DEFAULT_BACKGROUND);
  const [textColor, setTextColor] = React.useState(DEFAULT_TEXT_COLOR);
  const [banners, setBanners] = React.useState(() =>
    startsWithBanner(modalOpt?.record?.block_type) ? [makeBlockBanner()] : []
  );
  const [showLocalName, setShowLocalName] = React.useState(true);
  const [hoverAnimation, setHoverAnimation] = React.useState(false);
  const [loadedItems, setLoadedItems] = React.useState([]);
  const [skus, setSkus] = React.useState([]);
  const [itemsLimit, setItemsLimit] = React.useState(DEFAULT_ITEMS_LIMIT);

  const { data: categories = [] } = useQuery(
    "info-zone-block-categories",
    async () => {
      const response = await axios.get(
        "/api/v1/categories/simple_list?page=1&per_page=999999"
      );
      return response.data || [];
    },
    { enabled: blockType === "best_in_category" }
  );

  const categoryOptions = React.useMemo(
    () => convertToCodeTreeData(categories),
    [categories]
  );

  const { data: block, isLoading } = useQuery(
    ["info-zone-block", modalOpt?.record_id],
    async () => {
      const response = await axios.get(
        `/api/v1/banner_blocks/${modalOpt.record_id}`
      );
      return response.data;
    },
    {
      enabled: isEdit && !!modalOpt?.record_id,
      cacheTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  React.useEffect(() => {
    if (!isEdit || !block) return;

    const loadedColor = block.background_color || DEFAULT_BACKGROUND;
    const loadedTextColor = block.text_color || DEFAULT_TEXT_COLOR;
    const items = block.banner_block_items || [];
    const { banners: nextBanners, values: bannerValues } = hydrateBlockBanners(
      block.block_type,
      items,
      (banner) => banner.key
    );

    setBlockType(block.block_type);
    setBackgroundColor(loadedColor);
    setTextColor(loadedTextColor);
    setShowLocalName(block.settings?.show_local_name !== false);
    setHoverAnimation(block.settings?.hover_animation === true);
    setLoadedItems(items);
    setBanners(nextBanners);
    setSkus(hydrateBlockSkus(items));
    setItemsLimit(block.settings?.items_limit || DEFAULT_ITEMS_LIMIT);

    form.resetFields();
    form.setFieldsValue({
      title: block.title || "",
      system_name: block.system_name || "",
      background_color: loadedColor,
      text_color: loadedTextColor,
      text_align: block.settings?.text_align || DEFAULT_TEXT_ALIGN,
      padding_top: paddingValue(block.padding_top),
      padding_bottom: paddingValue(block.padding_bottom),
      padding_left: paddingValue(block.padding_left),
      padding_right: paddingValue(block.padding_right),
      columns_count: block.columns_count || undefined,
      category_code: block.category_code || undefined,
      items_limit: block.settings?.items_limit || undefined,
      local_name: block.local_name || "",
      validity: toMomentRange(block.valid_from, block.valid_to),
      ...bannerValues,
    });
  }, [isEdit, block, form]);

  const addBanner = (overrides = {}) => {
    setBanners((prev) => [...prev, makeBlockBanner(overrides)]);
  };

  const removeBanner = (key) => {
    setBanners((prev) => prev.filter((banner) => banner.key !== key));
  };

  const updateBanner = (key, patch) => {
    setBanners((prev) =>
      prev.map((banner) =>
        banner.key === key ? { ...banner, ...patch } : banner
      )
    );
  };

  const buildFormData = (values) => {
    const formData = new FormData();

    const [validFrom, validTo] =
      VALIDITY_TYPES.includes(blockType) && Array.isArray(values.validity)
        ? values.validity
        : [null, null];

    formData.append("banner_block[title]", values.title || "");
    formData.append("banner_block[system_name]", values.system_name || "");
    formData.append("banner_block[block_type]", blockType);
    formData.append("banner_block[background_color]", backgroundColor || "");
    formData.append("banner_block[text_color]", textColor || "");
    formData.append(
      "banner_block[padding_top]",
      paddingValue(values.padding_top) ?? ""
    );
    formData.append(
      "banner_block[padding_bottom]",
      paddingValue(values.padding_bottom) ?? ""
    );
    formData.append(
      "banner_block[padding_left]",
      paddingValue(values.padding_left) ?? ""
    );
    formData.append(
      "banner_block[padding_right]",
      paddingValue(values.padding_right) ?? ""
    );
    formData.append(
      "banner_block[columns_count]",
      COLUMN_TYPES.includes(blockType) ? values.columns_count || "" : ""
    );
    formData.append(
      "banner_block[category_code]",
      blockType === "best_in_category" ? values.category_code || "" : ""
    );
    formData.append("banner_block[valid_from]", normalizeDateForApi(validFrom));
    formData.append("banner_block[valid_to]", normalizeDateForApi(validTo));

    if (SKU_TYPES.includes(blockType)) {
      formData.append(
        "banner_block[settings][items_limit]",
        values.items_limit || DEFAULT_ITEMS_LIMIT
      );
    }

    if (TEXT_ALIGN_TYPES.includes(blockType)) {
      formData.append(
        "banner_block[settings][text_align]",
        values.text_align || DEFAULT_TEXT_ALIGN
      );
    }

    if (HOVER_ANIMATION_TYPES.includes(blockType)) {
      formData.append(
        "banner_block[settings][hover_animation]",
        hoverAnimation ? "true" : "false"
      );
    }

    if (LOCAL_NAME_TYPES.includes(blockType)) {
      formData.append("banner_block[local_name]", values.local_name || "");
      formData.append(
        "banner_block[settings][show_local_name]",
        showLocalName ? "true" : "false"
      );
    }

    appendBlockItems(formData, {
      blockType,
      banners,
      values,
      loadedItems,
      itemPrefix: (index) =>
        `banner_block[banner_block_items_attributes][${index}]`,
      fieldPrefixFor: (banner) => banner.key,
    });

    appendBlockSkus(formData, {
      blockType,
      skus,
      loadedItems,
      itemPrefix: (index) =>
        `banner_block[banner_block_items_attributes][${index}]`,
    });

    return formData;
  };

  const mutation = useMutation({
    mutationFn: (values) => {
      const formData = buildFormData(values);

      const request = isEdit
        ? axios.put(`/api/v1/banner_blocks/${modalOpt.record_id}`, formData)
        : axios.post("/api/v1/banner_blocks", formData);

      return request.then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries("info-zone-blocks");
      queryClient.invalidateQueries("info-zone-banners");
      message.success(isEdit ? "Block updated" : "Block created");
      onClose();
    },
    onError: (error) => {
      showApiError(error, isEdit ? "Failed to update block" : "Failed to create block");
    },
  });

  const onFinishFailed = (errorInfo) => {
    const firstError = errorInfo?.errorFields?.[0]?.errors?.[0];
    message.error(firstError || "Please fill required fields before saving.");
  };

  const typeLabel = blockType ? blockTypeLabel(blockType) : "block";

  return (
    <ModalSpecial
      style={{ width: 570 }}
      text={
        <span className="font-bold text-xl text-black">
          {isEdit ? `Edit ${typeLabel}` : `Add ${typeLabel}`}
        </span>
      }
      onClose={onClose}
    >
      {isEdit && isLoading ? (
        <div className="text-center text-muted py-4">Loading block data...</div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          name="info_zone_block_form"
          onFinish={(values) => mutation.mutate(values)}
          onFinishFailed={onFinishFailed}
          onValuesChange={(changed) => {
            if (!("items_limit" in changed)) return;
            const next = Number(changed.items_limit);
            setItemsLimit(
              Number.isInteger(next) && next > 0 ? next : DEFAULT_ITEMS_LIMIT
            );
          }}
          autoComplete="off"
          initialValues={{
            background_color: DEFAULT_BACKGROUND,
            text_color: DEFAULT_TEXT_COLOR,
          }}
        >
          <BlockFields
            blockType={blockType}
            nameFor={(field) => FIELD_NAMES[field]}
            backgroundColor={backgroundColor}
            onBackgroundColorChange={setBackgroundColor}
            textColor={textColor}
            onTextColorChange={setTextColor}
            showLocalName={showLocalName}
            onShowLocalNameChange={() => setShowLocalName((prev) => !prev)}
            hoverAnimation={hoverAnimation}
            onHoverAnimationChange={() => setHoverAnimation((prev) => !prev)}
            categoryOptions={categoryOptions}
            skuCount={skus.filter((entry) => entry.sku).length}
            itemsLimit={itemsLimit}
          />

          {SKU_TYPES.includes(blockType) && (
            <BlockSkus skus={skus} itemsLimit={itemsLimit} onChange={setSkus} />
          )}

          {ITEM_TYPES.includes(blockType) ? (
            <BlockBanners
              blockType={blockType}
              typeLabel={typeLabel}
              banners={banners}
              fieldPrefixFor={(banner) => banner.key}
              onAdd={addBanner}
              onRemove={removeBanner}
              onUpdate={updateBanner}
              form={form}
            />
          ) : SKU_TYPES.includes(blockType) ? null : (
            <div className="mb-3">
              {isEdit && (
                <div className="text-muted" style={{ fontSize: 13 }}>
                  Banners in block: {block?.items_count ?? 0}
                </div>
              )}
              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Banners and their images are edited from the info zone this
                block belongs to.
              </div>
            </div>
          )}

          <Form.Item className="mt-3">
            <Button
              className="w-100"
              type="primary"
              htmlType="submit"
              loading={mutation.isLoading}
            >
              {isEdit ? `Update ${typeLabel}` : `Add ${typeLabel}`}
            </Button>
          </Form.Item>
        </Form>
      )}
    </ModalSpecial>
  );
};

export default ModalBlock;
